package com.evoapps.evoshift.widgets

import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.ui.DeepNavy
import kotlin.math.abs
import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.log10
import kotlin.math.pow
import kotlin.math.round

/** A bar series; where [estimated] is true the bar is drawn faded (a plan, not a fact yet). */
@Immutable
class ChartBars(
    val label: String,
    val color: Color,
    val values: List<Double?>,
    val estimated: List<Boolean> = emptyList()
)

/** A line on its own axis (right side), e.g. a percentage over money bars. */
@Immutable
class ChartLine(
    val label: String,
    val color: Color,
    val values: List<Double?>,
    val format: (Double) -> String,
    /** The axis reaches at least this (e.g. 40 %), so small values don't look dramatic. */
    val minAxisMax: Double = 0.0,
    /** An optional reference level (e.g. a 30 % labour target), drawn dashed. */
    val reference: Double? = null
)

/**
 * Bars per slot (day, month) with an optional line on a second axis. Hovering, tapping or
 * dragging picks a slot; its figures show in the read-out above the chart ([readout]), which
 * stays readable on every screen size instead of a floating tooltip.
 */
@Composable
fun BarLineChart(
    xLabels: List<String>,
    bars: List<ChartBars>,
    modifier: Modifier = Modifier,
    line: ChartLine? = null,
    axisFormat: (Double) -> String,
    initialSelection: Int? = null,
    labelEvery: Int = 1,
    height: Dp = 200.dp,
    readout: @Composable (index: Int) -> Unit
) {
    val count = xLabels.size
    if (count == 0) return
    var selected by remember(count) { mutableStateOf((initialSelection ?: (count - 1)).coerceIn(0, count - 1)) }
    val measurer = rememberTextMeasurer()
    val axisStyle = MaterialTheme.typography.labelSmall.copy(fontSize = 10.sp, color = DeepNavy.copy(alpha = 0.55f))
    val selectedAxisStyle = axisStyle.copy(color = DeepNavy, fontWeight = FontWeight.Bold)

    val barMax = remember(bars) { niceCeil(bars.flatMap { it.values }.filterNotNull().maxOrNull() ?: 0.0) }
    val lineMax = remember(line) {
        line?.let { l -> niceCeil(maxOf(l.minAxisMax, l.values.filterNotNull().maxOrNull() ?: 0.0, l.reference ?: 0.0)) }
    }

    Column(modifier) {
        Box(Modifier.fillMaxWidth().heightIn(min = 44.dp)) { readout(selected) }
        Spacer(Modifier.height(8.dp))
        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(height)
                .semantics { contentDescription = bars.joinToString { it.label } + (line?.let { ", ${it.label}" } ?: "") }
                .pointerInput(count) {
                    // Mouse hover, a tap or a drag all pick the slot under the pointer.
                    awaitPointerEventScope {
                        while (true) {
                            val event = awaitPointerEvent()
                            if (event.type == PointerEventType.Exit) continue
                            val x = event.changes.firstOrNull()?.position?.x ?: continue
                            val left = 40.dp.toPx()
                            val right = if (line != null) 36.dp.toPx() else 8.dp.toPx()
                            val slot = (size.width - left - right) / count
                            if (slot > 0) selected = ((x - left) / slot).toInt().coerceIn(0, count - 1)
                        }
                    }
                }
        ) {
            val left = 40.dp.toPx()
            val right = if (line != null) 36.dp.toPx() else 8.dp.toPx()
            val bottom = 18.dp.toPx()
            val plotW = size.width - left - right
            val plotH = size.height - bottom
            val slot = plotW / count
            val gridColor = DeepNavy.copy(alpha = 0.08f)

            // Grid and the money axis (0, half, max).
            listOf(0.0, 0.5, 1.0).forEach { f ->
                val y = plotH - (plotH * f).toFloat()
                drawLine(gridColor, Offset(left, y), Offset(left + plotW, y), strokeWidth = 1.dp.toPx())
                val text = measurer.measure(axisFormat(barMax * f), axisStyle)
                drawText(text, topLeft = Offset(left - text.size.width - 6.dp.toPx(), y - text.size.height / 2))
            }

            // The picked slot.
            drawRoundRect(
                DeepNavy.copy(alpha = 0.05f),
                topLeft = Offset(left + slot * selected, 0f),
                size = Size(slot, plotH),
                cornerRadius = CornerRadius(4.dp.toPx())
            )

            // Bars, side by side within a slot.
            if (barMax > 0) {
                val group = slot * 0.72f
                val barW = group / bars.size
                val radius = CornerRadius(minOf(barW / 2, 3.dp.toPx()))
                bars.forEachIndexed { b, series ->
                    series.values.forEachIndexed { i, value ->
                        if (value == null || value <= 0) return@forEachIndexed
                        val h = (value / barMax * plotH).toFloat()
                        val x = left + slot * i + (slot - group) / 2 + barW * b
                        val faded = series.estimated.getOrNull(i) == true
                        drawRoundRect(
                            series.color.copy(alpha = if (faded) 0.3f else 1f),
                            topLeft = Offset(x + barW * 0.08f, plotH - h),
                            size = Size(barW * 0.84f, h),
                            cornerRadius = radius
                        )
                    }
                }
            }

            // The line and its axis.
            if (line != null && lineMax != null && lineMax > 0) {
                fun yOf(v: Double) = plotH - (v / lineMax * plotH).toFloat()
                line.reference?.let { ref ->
                    drawLine(
                        line.color.copy(alpha = 0.45f), Offset(left, yOf(ref)), Offset(left + plotW, yOf(ref)),
                        strokeWidth = 1.dp.toPx(), pathEffect = PathEffect.dashPathEffect(floatArrayOf(6f, 6f))
                    )
                }
                listOf(0.0, 0.5, 1.0).forEach { f ->
                    val text = measurer.measure(line.format(lineMax * f), axisStyle.copy(color = line.color.copy(alpha = 0.8f)))
                    drawText(text, topLeft = Offset(left + plotW + 6.dp.toPx(), yOf(lineMax * f) - text.size.height / 2))
                }
                val path = Path()
                var drawing = false
                line.values.forEachIndexed { i, v ->
                    val x = left + slot * i + slot / 2
                    if (v == null) {
                        drawing = false
                    } else if (!drawing) {
                        path.moveTo(x, yOf(v)); drawing = true
                    } else {
                        path.lineTo(x, yOf(v))
                    }
                }
                drawPath(path, line.color, style = Stroke(width = 2.dp.toPx(), cap = StrokeCap.Round))
                line.values.forEachIndexed { i, v ->
                    if (v == null) return@forEachIndexed
                    val center = Offset(left + slot * i + slot / 2, yOf(v))
                    drawCircle(Color.White, radius = if (i == selected) 4.5.dp.toPx() else 3.dp.toPx(), center = center)
                    drawCircle(line.color, radius = if (i == selected) 3.5.dp.toPx() else 2.dp.toPx(), center = center)
                }
            }

            // X labels: every [labelEvery]th slot and the picked one (its neighbours make room).
            xLabels.forEachIndexed { i, label ->
                val regular = i % labelEvery == 0 && (labelEvery == 1 || abs(i - selected) > labelEvery / 2)
                if (!regular && i != selected) return@forEachIndexed
                val text = measurer.measure(label, if (i == selected) selectedAxisStyle else axisStyle)
                val x = left + slot * i + slot / 2 - text.size.width / 2
                drawText(text, topLeft = Offset(x.coerceIn(0f, size.width - text.size.width), plotH + 3.dp.toPx()))
            }
        }
        Spacer(Modifier.height(10.dp))
        ChartLegend(bars, line)
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ChartLegend(bars: List<ChartBars>, line: ChartLine?) {
    FlowRow(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        bars.forEach { LegendItem(it.label, it.color, round = false) }
        line?.let { LegendItem(it.label, it.color, round = true) }
    }
}

@Composable
private fun LegendItem(label: String, color: Color, round: Boolean) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(
            Modifier
                .size(if (round) 8.dp else 10.dp)
                .clip(if (round) CircleShape else RoundedCornerShape(2.dp))
                .background(color)
        )
        Spacer(Modifier.width(6.dp))
        Text(label, style = MaterialTheme.typography.labelSmall, color = DeepNavy.copy(alpha = 0.7f))
    }
}

/** One labelled figure in a chart's read-out. */
@Composable
fun RowScope.ChartFigure(label: String, value: String, color: Color, modifier: Modifier = Modifier) {
    // Equal shares of the readout row and one line each: moving over the days never reflows it.
    Column(modifier.weight(1f)) {
        Text(
            label, style = MaterialTheme.typography.labelSmall, color = DeepNavy.copy(alpha = 0.6f),
            maxLines = 1, overflow = TextOverflow.Ellipsis
        )
        Text(
            value, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = color,
            maxLines = 1, softWrap = false, overflow = TextOverflow.Ellipsis
        )
    }
}

/** 1, 2, 2.5 or 5 × a power of ten at or above [value], so axis labels are round. */
internal fun niceCeil(value: Double): Double {
    if (value <= 0) return 0.0
    val magnitude = 10.0.pow(floor(log10(value)))
    val step = listOf(1.0, 2.0, 2.5, 5.0, 10.0).first { it * magnitude >= value }
    return ceil(step * magnitude * 1000) / 1000
}

/** "4,5k" / "850" / "1,2M": short money for an axis. */
fun compactAmount(value: Double): String = when {
    value >= 1_000_000 -> trimZero(value / 1_000_000) + "M"
    value >= 1_000 -> trimZero(value / 1_000) + "k"
    else -> value.toInt().toString()
}

private fun trimZero(v: Double): String {
    val rounded = round(v * 10) / 10
    return if (rounded % 1.0 == 0.0) rounded.toInt().toString() else rounded.toString().replace('.', ',')
}
